import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Newsletter subscribe form. Two visual variants:
 *   - variant="footer" — dark background, inline layout
 *   - variant="hero"   — light card with more copy, centered layout
 */
export default function NewsletterForm({ variant = "footer", source = "footer" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMsg("");
    try {
      await api.post("/newsletter/subscribe", { email, name, source });
      setStatus("ok");
      track("newsletter_subscribe", { source });
      setEmail(""); setName("");
    } catch (err) {
      setStatus("error");
      setMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (variant === "hero") {
    return (
      <div className="bg-white border border-[var(--line)] p-8 md:p-12 text-center max-w-2xl mx-auto" data-testid="newsletter-hero">
        <span className="eyebrow-dark">Insights, monthly-ish</span>
        <h3 className="font-display text-3xl text-[var(--navy)] mt-3">The Catalyst dispatch.</h3>
        <p className="text-[var(--muted)] text-sm mt-3 max-w-md mx-auto">
          Frameworks and field lessons on running effective behavioral-health programs.
          One email a month, sometimes two. Never spam.
        </p>
        {status === "ok" ? (
          <div className="mt-6 flex items-center justify-center gap-2 text-emerald-700" data-testid="newsletter-success">
            <CheckCircle2 size={16} />
            <span className="text-sm">You're on the list. Welcome.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <input
              required type="email" placeholder="you@organization.org"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="border border-[var(--line)] px-4 py-2.5 sm:min-w-[280px] text-sm"
              data-testid="newsletter-hero-email"
            />
            <button
              type="submit" disabled={status === "loading"}
              className="btn btn-navy disabled:opacity-60"
              data-testid="newsletter-hero-submit"
            >
              {status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        )}
        {status === "error" && (
          <p className="text-xs text-red-600 mt-3" data-testid="newsletter-error">{msg}</p>
        )}
      </div>
    );
  }

  // footer variant
  return (
    <div className="max-w-sm" data-testid="newsletter-footer">
      <p className="text-white/60 text-xs uppercase tracking-widest mb-3">Newsletter</p>
      <p className="text-white/80 text-sm mb-3">
        Monthly-ish insights on behavioral-health operations. No spam.
      </p>
      {status === "ok" ? (
        <p className="text-emerald-300 text-sm flex items-center gap-2" data-testid="newsletter-success">
          <CheckCircle2 size={14} /> Thanks — you're in.
        </p>
      ) : (
        <form onSubmit={submit} className="flex gap-2">
          <input
            required type="email" placeholder="you@example.org"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder-white/40 rounded-none"
            data-testid="newsletter-footer-email"
          />
          <button
            type="submit" disabled={status === "loading"}
            className="bg-[var(--gold)] text-[var(--navy-deep)] px-3 py-2 text-xs uppercase tracking-widest font-medium hover:brightness-110 disabled:opacity-60"
            data-testid="newsletter-footer-submit"
          >
            {status === "loading" ? "…" : "Join"}
          </button>
        </form>
      )}
      {status === "error" && (
        <p className="text-red-300 text-xs mt-2" data-testid="newsletter-error">{msg}</p>
      )}
    </div>
  );
}
